/**
 * Stripe Provider
 * Wraps the application with Stripe context for payment processing
 */

import React, { useMemo } from 'react'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

// Get the publishable key from environment
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? ''

/**
 * StripeProviderProps
 */
interface StripeProviderProps {
  children: React.ReactNode
}

/**
 * Load Stripe instance outside of component to avoid recreating on re-renders
 */
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null

/**
 * StripeProvider Component
 * Wraps children with Stripe Elements context
 */
export const StripeProvider: React.FC<StripeProviderProps> = React.memo(function StripeProvider({
  children,
}) {
  const options = useMemo(
    () => ({
      appearance: {
        theme: 'night' as const,
        variables: {
          colorBackground: '#1a1a2e',
          colorText: '#ffffff',
          colorPrimary: '#6366f1',
          colorPrimaryText: '#ffffff',
          colorDanger: '#ef4444',
          colorWarning: '#f59e0b',
          colorSuccess: '#10b981',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSizeBase: '16px',
          spacingUnit: '4px',
          borderRadius: '8px',
        },
      },
      locale: 'auto' as const,
      fonts: [
        {
          family: 'Inter',
          src: 'url(https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2)',
          weight: '400',
        },
      ],
    }),
    [],
  )

  // If no Stripe key is configured, just render children without Stripe context
  if (!stripePublishableKey) {
    return <>{children}</>
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  )
})

/**
 * useStripe Hook
 * Returns the Stripe instance and loading state
 */
export function useStripeContext() {
  return {
    stripePromise,
    isConfigured: !!stripePublishableKey,
  }
}
